<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import { SmartPickerModal, type SmartPickerOption } from '@yuksalish/ui'
import { listProducts } from '../productService'
import ProductQuickCreateModal from './ProductQuickCreateModal.vue'
import type { EngineeringProduct } from '../types'

const props = defineProps<{
  open: boolean
  title: string
  productType: string
  productTypes?: string[]
}>()

const emit = defineEmits<{
  close: []
  select: [product: EngineeringProduct]
}>()

const { t } = useI18n()
const client = useApiClient()
const auth = useAuthStore()
const search = ref('')
const products = ref<EngineeringProduct[]>([])
const loading = ref(false)
const errorMessage = ref('')
const createOpen = ref(false)
const page = ref(1)
const pageSize = 8
const canCreate = computed(() => auth.user?.permissions.includes('products.create') ?? false)
const totalPages = computed(() => Math.max(1, Math.ceil(products.value.length / pageSize)))
const visibleProducts = computed(() => {
  const start = (page.value - 1) * pageSize
  return products.value.slice(start, start + pageSize)
})
const visibleOptions = computed<SmartPickerOption[]>(() =>
  visibleProducts.value.map((product) => ({
    id: product.id,
    title: product.name,
    subtitle: product.sku || t('engineering.picker.noSku'),
    meta: `${product.unit} · #${product.id}`,
  })),
)
const pageLabel = computed(() =>
  t('engineering.picker.itemsPage', {
    count: products.value.length,
    page: page.value,
    total: totalPages.value,
  }),
)
const createTitle = computed(() =>
  t('engineering.picker.createTitle', {
    type: props.productType.replace('_', ' '),
  }),
)
const effectiveTypes = computed(() =>
  props.productTypes?.length ? props.productTypes : [props.productType],
)
let searchTimer: ReturnType<typeof setTimeout> | undefined

async function load(): Promise<void> {
  loading.value = true
  errorMessage.value = ''
  try {
    const results = await Promise.all(
      effectiveTypes.value.map((type) =>
        listProducts(client, {
          type,
          search: search.value.trim() || undefined,
        }, false),
      ),
    )
    products.value = results
      .flat()
      .sort((left, right) => right.id - left.id)
    page.value = Math.min(page.value, Math.max(1, Math.ceil(products.value.length / pageSize)))
  } catch (error: unknown) {
    products.value = []
    errorMessage.value = formatApiError(error)
  } finally {
    loading.value = false
  }
}

watch(() => props.open, (open) => {
  if (open) {
    search.value = ''
    page.value = 1
    void load()
  }
}, { immediate: true })

watch(search, () => {
  if (!props.open) return
  clearTimeout(searchTimer)
  page.value = 1
  searchTimer = setTimeout(load, 300)
})

function selectOption(option: SmartPickerOption): void {
  const product = visibleProducts.value.find((entry) => entry.id === Number(option.id))
  if (product) emit('select', product)
}

function handleCreated(product: EngineeringProduct): void {
  products.value = [product, ...products.value.filter((entry) => entry.id !== product.id)]
  createOpen.value = false
  emit('select', product)
}
</script>

<template>
  <SmartPickerModal
    :open="open"
    :title="title"
    :eyebrow="t('engineering.picker.repository')"
    :search-label="t('engineering.picker.search')"
    :search-placeholder="t('engineering.picker.searchPlaceholder')"
    :search-value="search"
    :loading="loading"
    :error-message="errorMessage"
    :empty-message="t('engineering.picker.empty')"
    :items="visibleOptions"
    :page="page"
    :total-pages="totalPages"
    :page-label="pageLabel"
    :close-aria-label="t('engineering.picker.close')"
    :retry-label="t('engineering.picker.retry')"
    :previous-label="t('engineering.picker.previous')"
    :next-label="t('engineering.picker.next')"
    :cancel-label="t('engineering.picker.cancel')"
    @close="emit('close')"
    @retry="load"
    @previous="page--"
    @next="page++"
    @select="selectOption"
    @update:search-value="search = $event"
  >
    <template #loading>{{ t('engineering.picker.loading') }}</template>
    <template #helper>
      <div class="picker-helper">
        <div>
          <strong>{{ t('engineering.picker.missingTitle') }}</strong>
          <p>{{ t('engineering.picker.missingHelp') }}</p>
        </div>
        <button type="button" :disabled="!canCreate" @click="createOpen = true">
          {{ t('engineering.picker.addNew') }}
        </button>
      </div>
    </template>
  </SmartPickerModal>
  <ProductQuickCreateModal
    :open="createOpen"
    :title="createTitle"
    :product-type="productType as any"
    @close="createOpen = false"
    @created="handleCreated"
  />
</template>

<style scoped>
.picker-helper { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 12px; border: 1px solid #d7dfdc; background: #f7fbff; }
.picker-helper strong { display: block; color: #173454; font-size: 13px; }
.picker-helper p { margin: 2px 0 0; color: #66726e; font-size: 12px; line-height: 1.4; }
.picker-helper button { padding: 8px 12px; border: 1px solid #b9c5c1; color: #285b9a; background: #fff; font: inherit; font-weight: 700; cursor: pointer; }
.picker-helper button:disabled { color: #9aa7a3; background: #f3f6f5; cursor: default; }
</style>
